import { PolicyCreate } from "@/types";
import { VendorRef } from "@/types/common";
import {
  validateDateRange,
  validateMinValue,
  validateRequired,
  validateEmail,
  validateZipCodeFormat,
} from "../helpers/validationHelpers";

export const validatePolicies = (values: PolicyCreate & Partial<VendorRef>): Record<string, string> => {
  const errors: Record<string, string> = {};

  const source_data = values.source_data || {};
  const aggregate_benefit = values.aggregate_benefit || {};
  const specific_benefit = values.specific_benefit || {};

  // --- Top-Level Required Fields ---
  validateRequired(values.client_id_ref, "client_id_ref", errors);
  validateRequired(values.vendor_id_ref, "vendor_id_ref", errors, "Carrier is required");
  validateRequired(values.effective_start_date, "effective_start_date", errors, "Effective start date is required");
  validateRequired(values.effective_end_date, "effective_end_date", errors, "Effective end date is required");

  // --- Source Data Required Fields (per backend schema) ---
  validateRequired(source_data.policyholder, "source_data.policyholder", errors, "Policyholder is required");

  // --- Date Range Validation ---
  validateDateRange(values.effective_start_date, values.effective_end_date, "effective", errors);

  // --- Email Validation ---
  validateEmail(source_data.policyholder_contact_email, "source_data.policyholder_contact_email", errors);

  // --- Zip Code Validation ---
  if (source_data.policyholder_zip_code) {
    validateZipCodeFormat(source_data.policyholder_zip_code, "source_data.policyholder_zip_code", errors);
  }

  // --- Specific Benefit Conditional Validation ---
  if (specific_benefit.is_specific_deductible) {
    if (specific_benefit.is_aggregate_specific_deductible) {
      validateMinValue(
        specific_benefit.aggregate_specific_deductible,
        "specific_benefit.aggregate_specific_deductible",
        errors
      );
    }

    if (specific_benefit.covered_composite) {
      validateMinValue(
        specific_benefit.specific_composite_premium,
        "specific_benefit.specific_composite_premium",
        errors
      );
    }

    if (specific_benefit.specific_employee_plus_children_enrolled) {
      validateMinValue(
        specific_benefit.specific_employee_plus_children_premium,
        "specific_benefit.specific_employee_plus_children_premium",
        errors
      );
    }

    if (specific_benefit.specific_employee_plus_spouse_premium) {
      validateMinValue(
        specific_benefit.specific_employee_plus_spouse_premium,
        "specific_benefit.specific_employee_plus_spouse_premium",
        errors
      );
    }

    if (specific_benefit.families_enrolled) {
      validateMinValue(specific_benefit.specific_family_premium, "specific_benefit.specific_family_premium", errors);
    }

    if (specific_benefit.individuals_enrolled) {
      validateMinValue(
        specific_benefit.specific_individual_premium,
        "specific_benefit.specific_individual_premium",
        errors
      );
    }

    // Date range validations for specific benefit
    validateDateRange(
      specific_benefit.eligible_expenses_incurred_start_date,
      specific_benefit.eligible_expenses_incurred_end_date,
      "specific_benefit.eligible_expenses_incurred",
      errors
    );

    validateDateRange(
      specific_benefit.eligible_expenses_paid_start_date,
      specific_benefit.eligible_expenses_paid_end_date,
      "specific_benefit.eligible_expenses_paid",
      errors
    );
  }

  // --- Aggregate Benefit Conditional Validation ---
  if (aggregate_benefit.is_aggregate_deductible) {
    validateMinValue(
      aggregate_benefit.aggregate_deductible,
      "aggregate_benefit.aggregate_deductible",
      errors,
      1,
      "Value must be greater than 0"
    );

    validateMinValue(
      aggregate_benefit.aggregate_composite_premium,
      "aggregate_benefit.aggregate_composite_premium",
      errors
    );

    // Premium validations with enrollment checks
    if (aggregate_benefit.aggregate_employee_plus_children_enrolled) {
      validateMinValue(
        aggregate_benefit.aggregate_employee_plus_children_premium,
        "aggregate_benefit.aggregate_employee_plus_children_premium",
        errors
      );
    }

    if (aggregate_benefit.aggregate_employee_plus_spouse_enrolled) {
      validateMinValue(
        aggregate_benefit.aggregate_employee_plus_spouse_premium,
        "aggregate_benefit.aggregate_employee_plus_spouse_premium",
        errors
      );
    }

    // Family/Individual enrollment validation
    if (aggregate_benefit.aggregate_family_premium && !aggregate_benefit.families_enrolled) {
      errors["aggregate_benefit.families_enrolled"] = "Families enrolled is required with family premium";
    }

    if (aggregate_benefit.aggregate_individual_premium && !aggregate_benefit.individuals_enrolled) {
      errors["aggregate_benefit.individuals_enrolled"] = "Employees enrolled is required with individual premium";
    }

    // Date range validations for aggregate benefit
    validateDateRange(
      aggregate_benefit.eligible_expenses_incurred_start_date,
      aggregate_benefit.eligible_expenses_incurred_end_date,
      "aggregate_benefit.eligible_expenses_incurred",
      errors
    );

    validateDateRange(
      aggregate_benefit.eligible_expenses_paid_start_date,
      aggregate_benefit.eligible_expenses_paid_end_date,
      "aggregate_benefit.eligible_expenses_paid",
      errors
    );
    validateMinValue(
      aggregate_benefit.monthly_aggregate_spend_per_composite,
      "aggregate_benefit.monthly_aggregate_spend_per_composite",
      errors
    );
    validateMinValue(
      aggregate_benefit.aggregate_individual_monthly_factor,
      "aggregate_benefit.aggregate_individual_monthly_factor",
      errors
    );
    validateMinValue(
      aggregate_benefit.aggregate_employee_plus_children_monthly_factor,
      "aggregate_benefit.aggregate_employee_plus_children_monthly_factor",
      errors
    );
    validateMinValue(
      aggregate_benefit.aggregate_employee_plus_spouse_monthly_factor,
      "aggregate_benefit.aggregate_employee_plus_spouse_monthly_factor",
      errors
    );
    validateMinValue(
      aggregate_benefit.aggregate_family_monthly_factor,
      "aggregate_benefit.aggregate_family_monthly_factor",
      errors
    );
  }

  // --- Reporting Cadence Conditional Validation ---
  validateReportingCadenceFields(values, errors);

  return errors;
};

/** Subset of policy fields that drive reporting task generation. */
export interface ReportingCadenceValues {
  reporting_cadence?: string | null;
  reporting_day_of_month?: number | null;
  reporting_day_of_week?: number | null;
  reporting_start_date?: string | null;
  task_lead_days?: number | null;
}

/**
 * Validates the reporting cadence fields, mutating and returning `errors`.
 * Shared by the policy create form (validatePolicies) and the detail-page
 * Reporting Cadence dialog so both enforce identical rules — cadence and its
 * day field must be set together, mirroring the server's validateReportingCadence.
 */
export const validateReportingCadenceFields = (
  values: ReportingCadenceValues,
  errors: Record<string, string>
): Record<string, string> => {
  const cadence = values.reporting_cadence;
  if (cadence) {
    validateRequired(
      values.reporting_start_date,
      "reporting_start_date",
      errors,
      "Reporting start date is required when a cadence is set"
    );
    if (cadence === "monthly") {
      if (!values.reporting_day_of_month || values.reporting_day_of_month < 1 || values.reporting_day_of_month > 28) {
        errors["reporting_day_of_month"] = "Day of month must be between 1 and 28";
      }
    }
    if (cadence === "weekly") {
      if (!values.reporting_day_of_week || values.reporting_day_of_week < 1 || values.reporting_day_of_week > 7) {
        errors["reporting_day_of_week"] = "Day of week must be between 1 and 7";
      }
    }
  }
  if (values.task_lead_days !== undefined && values.task_lead_days !== null) {
    if (values.task_lead_days < 0 || values.task_lead_days > 28) {
      errors["task_lead_days"] = "Lead days must be between 0 and 28";
    }
  }

  return errors;
};
